// @ts-check
import { useCallback, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Card } from 'ui-components/components/card';
import { FlexRow, FlexColumn, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { messages } from './messages';
import style from './no-orders-yet-pane.module.less';
import { Segment } from '../../../../../../services/analytics-services/segment';
import { ClickEvents, EventSourceStrings } from '../../../../../../tracking/tracking-constants';
import { prepareToOpenNewOrderModal } from '../../../../../../store/orders/edit-order/edit.order.actions';
import Global from '../../../../../../store/global-selectors';
import noOrdersYetIllustration from '../../../svgs/no-returns-or-orders-yet.svg';
import { TodoCard } from '../../../todo-cards/todo-card';
import { todoCardIds } from '../../../todo-cards/todo-card-ids';

const HELP_CENTER_ARTICLE_LINK = 'https://help.shipstation.com/hc/en-us/articles/360025856252-Manual-Orders';

/**
 * Component displayed when user selects "I don't have orders yet" option
 * Shows information about manual order creation with a "Do Later" option
 * @param {object} props
 * @param {(hasManualOrder: boolean) => void} [props.onManualOrderAdded]
 * @returns {JSX.Element}
 */
export const NoOrdersYetPane = ({ onManualOrderAdded }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const hasMadeChanges = useSelector(Global.selectEditOrdergetEditOrderHasMadeChanges);
  const previousHasMadeChangesRef = useRef(false);
  const hasNotifiedRef = useRef(false);

  // Detect when hasMadeChanges goes from true to false (modal closed after saving)
  useEffect(() => {
    if (!hasNotifiedRef.current && previousHasMadeChangesRef.current && !hasMadeChanges && onManualOrderAdded) {
      // hasMadeChanges went from true to false, meaning modal closed after changes were made
      hasNotifiedRef.current = true;
      onManualOrderAdded(true);
    }
    previousHasMadeChangesRef.current = hasMadeChanges;
  }, [hasMadeChanges, onManualOrderAdded]);

  const handleAddManualOrder = useCallback(() => {
    Segment.trackEvent(ClickEvents.FTU_ADD_MANUAL_ORDER_CLICKED, {
      source: EventSourceStrings.FTU_WIZARD_SIMPLIFIED_DROPDOWN,
    });
    dispatch(prepareToOpenNewOrderModal());
  }, [dispatch]);

  return (
    <Card className={style.card}>
      <FlexRow className={style['columns-wrapper']}>
        <FlexColumn className={style['content-column']} gap={Gap.Space16}>
          <Text textType={TextType.H3} value={messages.noOrdersYetTitle} />
          <Text className={style['content-column-description']} textType={TextType.Paragraph} value={messages.noOrdersYetDescription} />
          <Button
            className={style['description-link']}
            href={HELP_CENTER_ARTICLE_LINK}
            rel='noreferrer'
            target='_blank'
            theme={THEME_CONSTANTS.unstyled}
          >
            <FormattedMessage {...messages.learnMore} />
          </Button>
          <Button className={style['manual-order-button']} onClick={handleAddManualOrder} theme={THEME_CONSTANTS.primary}>
            <FormattedMessage {...messages.addManualOrder} />
          </Button>
          <div className={style['todo-card-wrapper']}>
            <TodoCard
              description={messages.noOrdersYetDescription}
              id={String(todoCardIds.IMPORT_PRODUCTS)}
              onlyFooter
              title={messages.noOrdersYetTitle}
            />
          </div>
        </FlexColumn>
        <FlexColumn className={style.illustration}>
          <img alt={intl.formatMessage(messages.ordersIllustrationAlt)} src={noOrdersYetIllustration} />
        </FlexColumn>
      </FlexRow>
    </Card>
  );
};
